import { useSerialize } from "@lxc/app-device-common";
import type { Ref } from "vue";
import { useRoute } from "vue-router";
import {
  DEFAULT_FIRST_PAGE,
  DEFAULT_PAGE_SIZE,
} from "~/core/constants/constants";
import LxcError from "~/core/models/LxcError.class";
import type { PaginatedList } from "~/core/models/PaginatedList.interface";
import type { FiltersSelection } from "~/core/models/filters";
import { fetchAllPaginatedList } from "~/core/utils/fetch";

export interface FetchPageServiceParameters {
  businessUnitCode?: number;
  organizationCode?: number;
  sectorCode?: number;
  page: number;
  pageSize: number;
  searchParams: string | FiltersSelection;
  sort?: string | null;
  params?: Map<string, unknown>;
}

interface UseFetchParameters<T, U = undefined> {
  businessUnitCode?: number;
  organizationCode?: number;
  sectorCode?: number;
  service: (
    fetchPageServiceParameters: FetchPageServiceParameters,
  ) => Promise<PaginatedList<T, U> | LxcError>;
  searchParams: Ref<string | FiltersSelection>;
  sort: Ref<string | undefined>;
  sortOverrideCallback?: () => void;
  useQueryParametersForPagination?: boolean;
}

const serialize = useSerialize();

export function useFetchPage<T, U = undefined>({
  businessUnitCode,
  organizationCode,
  sectorCode,
  service,
  searchParams,
  sort,
  sortOverrideCallback,
  useQueryParametersForPagination,
}: UseFetchParameters<T, U>) {
  const allResults: Ref<T[] | null> = ref(null);
  const results: Ref<PaginatedList<T, U> | null> = ref(null);
  const error: Ref<LxcError | null | undefined> = ref(null);
  const isLoading = ref(false);

  const router = useRouter();
  const route = useRoute();

  if (useQueryParametersForPagination) {
    watch(route, setPageAndPageSizeFromQueryParams);
  }

  watch(
    () => sort.value,
    () => {
      return sortOverrideCallback ? sortOverrideCallback() : fetchData();
    },
  );

  let currentPage: number;
  let currentPageSize: number;

  let storeFetchDataParams: Map<string, unknown> | undefined;

  async function fetchAllData(params?: Map<string, unknown>) {
    isLoading.value = true;

    const response = await fetchAllPaginatedList(
      (page: number, pageSize: number) =>
        service({
          businessUnitCode,
          organizationCode,
          sectorCode,
          page,
          pageSize,
          searchParams: searchParams.value,
          sort: sort.value,
          params,
        }),
    );

    if (LxcError.check(response)) {
      allResults.value = null;
      error.value = response;
    } else {
      allResults.value = response ?? null;
      error.value = null;
    }

    isLoading.value = false;
  }

  let previousSearchParamsSerialized: string | undefined = undefined;

  /**
   * Fetch paginated data by calling the service
   * @param page
   * @param pageSize
   * @param params
   * @returns Effective page and pageSize used to fetch the data, useful when these parameters are needed after fetching data.
   */
  async function fetchData(
    page?: number,
    pageSize?: number,
    params?: Map<string, unknown>,
  ): Promise<{ page: number; pageSize: number }> {
    // The input parameters can either be extracted from the `LxcTable` component (via the usage of the `page` and
    // `pageSize` function parameters) or from the URI via query parameter (in this case, `page` and `pageSize` function
    // parameters are undefined).
    if (useQueryParametersForPagination) {
      if (page === undefined) {
        page = parseInt(route.query.page as string);
      }
      if (pageSize === undefined) {
        pageSize = parseInt(route.query.pageSize as string);
      }
    }

    // reset the page to the default one if is not defined, not a number
    // or if the search parameters has changed in order to avoid empty result
    if (
      page === undefined ||
      isNaN(page) ||
      (previousSearchParamsSerialized !== undefined &&
        previousSearchParamsSerialized !== serialize(searchParams.value))
    ) {
      page = DEFAULT_FIRST_PAGE;
    }

    // reset the page size to the default one if it is not defined, not a number
    if (pageSize === undefined || isNaN(pageSize)) {
      pageSize = DEFAULT_PAGE_SIZE;
    }

    previousSearchParamsSerialized = serialize(searchParams.value);

    await callService(page, pageSize, params);

    if (useQueryParametersForPagination) {
      await updatePageAndPageSizeQueryParamsAndRedirect(page, pageSize, params);
    }

    return {
      page,
      pageSize,
    };
  }

  /**
   * Update the page and pageSize query parameters
   */
  async function updatePageAndPageSizeQueryParamsAndRedirect(
    page: number,
    pageSize: number,
    params?: Map<string, unknown>,
  ) {
    storeFetchDataParams = params;

    const query = { ...route.query };
    query.page = page.toString();
    query.pageSize = pageSize.toString();

    const newRoute = {
      path: route.path,
      query,
    };

    // page and pageSize are not defined in query parameters => the route is replaced by the new one including page and pageSize query parameters
    if (route.query.page === undefined && route.query.pageSize === undefined) {
      await router.replace(newRoute);
    }
    // page or pageSize are defined in query parameters => the new route is pushed for navigation history
    else {
      await router.push(newRoute);
    }
  }

  /**
   * Use the page and pageSize query parameters to call the service
   * only if at least one of the parameters is present and has changed
   */
  async function setPageAndPageSizeFromQueryParams() {
    if (route.query.page || route.query.pageSize) {
      const page = parseInt(route.query.page as string);
      const pageSize = parseInt(route.query.pageSize as string);

      if (page !== currentPage || pageSize !== currentPageSize) {
        await callService(page, pageSize, storeFetchDataParams);
      }
    }
  }

  /**
   * Call the provided service and set results and error
   */
  async function callService(
    page: number = DEFAULT_FIRST_PAGE,
    pageSize: number = DEFAULT_PAGE_SIZE,
    params?: Map<string, unknown>,
  ) {
    isLoading.value = true;

    const response = await service({
      businessUnitCode,
      organizationCode,
      sectorCode,
      page,
      pageSize,
      searchParams: searchParams.value,
      sort: sort.value,
      params,
    });

    currentPage = page;
    currentPageSize = pageSize;

    if (LxcError.check(response)) {
      results.value = null;
      error.value = response;
    } else {
      results.value = response ?? null;
      error.value = null;
    }

    isLoading.value = false;
  }

  return {
    isLoading,
    results,
    allResults,
    error,
    fetchData,
    fetchAllData,
  };
}
